import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaBlog, FaSave, FaTimes } from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import LoadingSpinner from '../common/LoadingSpinner';

/**
 * Blog form component for creating and editing blog posts
 */
const BlogForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [blog, setBlog] = useState({
    title: '',
    subtitle: '',
    content: '',
    category: 'education',
    tags: '',
    status: 'draft',
    metaDescription: '',
    enableComments: true,
    isFeatured: false
  });
  
  // Categories for the dropdown
  const categories = [
    'education',
    'parenting',
    'technology',
    'mentorship',
    'child_development',
    'health_wellness',
    'school_management',
    'teaching_methods',
    'curriculum',
    'success_stories',
    'news',
    'events',
    'other'
  ];
  
  // Fetch blog data if editing
  useEffect(() => {
    if (id) {
      const fetchBlog = async () => {
        try {
          setLoading(true);
          const response = await api.get(`/admin/blogs/${id}`);
          
          // Convert tags array to comma-separated string for the form
          const blogData = response.data.data;
          if (blogData.tags && Array.isArray(blogData.tags)) {
            blogData.tags = blogData.tags.join(', ');
          }
          
          setBlog(blogData);
        } catch (error) {
          console.error('Error fetching blog post:', error);
          showError('Failed to load blog post');
        } finally {
          setLoading(false);
        }
      };
      
      fetchBlog();
    }
  }, [id, showError]);
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setBlog({
      ...blog,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    // Convert tags from comma-separated string to array
    const formData = { ...blog };
    if (typeof formData.tags === 'string') {
      formData.tags = formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
    }
    
    try {
      if (id) {
        // Update existing blog
        await api.put(`/admin/blogs/${id}`, formData);
        showSuccess('Blog post updated successfully');
      } else {
        // Create new blog
        await api.post('/admin/blogs', formData);
        showSuccess('Blog post created successfully');
      }
      navigate('/admin/blogs');
    } catch (error) {
      console.error('Error saving blog post:', error);
      showError(error.response?.data?.message || 'Failed to save blog post');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center">
          <FaBlog className="mr-2 text-primary" /> 
          {id ? 'Edit Blog Post' : 'Create New Blog Post'}
        </h1>
        <p className="text-gray-600 mt-1">
          {id ? 'Update an existing blog post' : 'Create a new blog post for the platform'}
        </p>
      </div>
      
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-soft p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Title*
            </label>
            <input
              type="text"
              id="title"
              name="title"
              required
              className="form-input w-full rounded-md"
              value={blog.title}
              onChange={handleChange}
              placeholder="Enter blog title"
            />
          </div>
          
          <div>
            <label htmlFor="subtitle" className="block text-sm font-medium text-gray-700 mb-1">
              Subtitle
            </label>
            <input
              type="text"
              id="subtitle"
              name="subtitle"
              className="form-input w-full rounded-md"
              value={blog.subtitle || ''}
              onChange={handleChange}
              placeholder="Enter blog subtitle"
            />
          </div>
        </div>
        
        <div className="mb-6">
          <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
            Content*
          </label>
          <textarea
            id="content"
            name="content"
            required
            rows="12"
            className="form-textarea w-full rounded-md"
            value={blog.content}
            onChange={handleChange}
            placeholder="Write your blog content here..."
          ></textarea>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
              Category*
            </label>
            <select
              id="category"
              name="category"
              required
              className="form-select w-full rounded-md"
              value={blog.category}
              onChange={handleChange}
            >
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-1">
              Tags (comma separated)
            </label>
            <input
              type="text"
              id="tags"
              name="tags"
              className="form-input w-full rounded-md"
              value={blog.tags || ''}
              onChange={handleChange}
              placeholder="education, technology, students..."
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              id="status"
              name="status"
              className="form-select w-full rounded-md"
              value={blog.status}
              onChange={handleChange}
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          
          <div>
            <label htmlFor="metaDescription" className="block text-sm font-medium text-gray-700 mb-1">
              Meta Description
            </label>
            <input
              type="text"
              id="metaDescription"
              name="metaDescription"
              className="form-input w-full rounded-md"
              value={blog.metaDescription || ''}
              onChange={handleChange}
              placeholder="Brief description for SEO"
              maxLength="160"
            />
          </div>
        </div>
        
        <div className="flex flex-wrap gap-6 mb-6">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="enableComments"
              name="enableComments"
              className="form-checkbox h-5 w-5 text-primary"
              checked={blog.enableComments}
              onChange={handleChange}
            />
            <label htmlFor="enableComments" className="ml-2 block text-sm text-gray-700">
              Enable Comments
            </label>
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isFeatured"
              name="isFeatured"
              className="form-checkbox h-5 w-5 text-primary"
              checked={blog.isFeatured}
              onChange={handleChange}
            />
            <label htmlFor="isFeatured" className="ml-2 block text-sm text-gray-700">
              Feature this post
            </label>
          </div>
        </div>
        
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/admin/blogs')}
            className="btn-secondary flex items-center"
            disabled={submitting}
          >
            <FaTimes className="mr-2" /> Cancel
          </button>
          <button
            type="submit"
            className="btn-primary flex items-center"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <LoadingSpinner size="sm" color="white" className="mr-2" /> Saving...
              </>
            ) : (
              <>
                <FaSave className="mr-2" /> {id ? 'Update Post' : 'Create Post'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BlogForm; 